import React from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import AnswerCapsule from '../../components/AnswerCapsule';
import './CityLanding.css';
import './FAQ.css';

const BASE_URL = 'https://www.buyselltaxi.com';
const PAGE_URL = `${BASE_URL}/faq`;

/**
 * Site-wide FAQ.
 *
 * Deliberately does NOT repeat the FAQ blocks already on /, /vehicles,
 * /sell-taxi, /taxi-valuation, the city pages or the model hubs. Those answer
 * "what does a used taxi cost", "what is my taxi worth", "what is the Dzire's
 * CNG mileage" — priced, model- and city-specific questions that rank on the
 * pages they live on. Repeating them here would put the same FAQPage answers
 * on two URLs and have BST competing with itself for the snippet.
 *
 * What was left unanswered anywhere is the operational half: what "verified"
 * actually means, how a buyer reaches a seller, what happens after an enquiry,
 * who does the RC transfer. That is what this page covers, and it links out to
 * the priced pages rather than restating them.
 *
 * Every answer below describes behaviour that exists in the product. Do not add
 * a commercial policy here — fees, refunds, finance, warranty, delivery —
 * without a source for it; "contact us" is the honest answer until there is one.
 */
const FAQ_GROUPS = [
  {
    id: 'buying',
    heading: 'Buying on Buy Sell Taxi',
    faqs: [
      {
        q: 'How do I contact the seller of a vehicle listed on Buy Sell Taxi?',
        a: 'You do not contact the seller directly. On any listing you can submit an enquiry, request a callback, or book a test drive, and the BST team takes it from there — we confirm the vehicle is still available, answer questions about its papers, and set up the visit. Keeping first contact with our team is what lets us verify both sides before anyone travels to see a car.',
      },
      {
        q: 'Why is the seller\'s phone number hidden on listings?',
        a: 'Seller numbers are shown only to dealers with an approved BST dealer account, through the "Show Seller Number" button on a listing. Every reveal is logged against that dealer. For everyone else the enquiry, callback and test-drive forms are the route in — it keeps sellers from being cold-called by anyone who lands on the page.',
      },
      {
        q: 'How long does Buy Sell Taxi take to respond to an enquiry?',
        a: 'You will get a call or WhatsApp message from the BST team confirming the vehicle, answering questions on permit and fitness validity, and offering a test-drive slot if you want one.',
      },
      {
        q: 'Can I see the vehicle in person before buying?',
        a: 'Yes. Book a test drive from the listing and we will arrange it. Many vehicles are kept at a BST Yard, where you can inspect the car and its paperwork in one visit; for a vehicle still with its seller we coordinate the viewing for you. Our yard locations are listed on the About page.',
      },
      {
        q: 'Do I need a commercial licence to buy a taxi on Buy Sell Taxi?',
        a: 'No. You can own a commercial vehicle on an ordinary licence — a commercial licence with a PSV badge is only needed by whoever drives it for hire. Fleet owners routinely buy vehicles they never drive themselves. There is more on this in our guide to buying a used taxi.',
      },
      {
        q: 'Which cities does Buy Sell Taxi cover?',
        a: 'Most of the live stock is in Delhi NCR — Delhi and Gurugram — with a smaller number of listings in other cities. Each city page shows what is currently available there, and buyers anywhere in India can enquire.',
      },
    ],
  },
  {
    id: 'verification',
    heading: 'Verification, condition and paperwork',
    faqs: [
      {
        q: 'What does a "BST Verified" listing actually mean?',
        a: 'It means the vehicle has been physically inspected by our team rather than listed on the seller\'s word. We check the vehicle\'s condition in person and go through its documents — RC, permit and fitness — before the listing is published. It is a check on what is being sold, not a warranty on how the vehicle will perform later.',
      },
      {
        q: 'Do vehicles on Buy Sell Taxi come with a valid permit?',
        a: 'It varies by vehicle. Some carry a live contract-carriage or All India Tourist Permit, others have lapsed papers priced into the asking figure. Listings do not show permit status yet, so ask the seller or us for the permit and fitness papers before you commit — our T-Permit guide explains what each type allows.',
      },
      {
        q: 'What should I check on a used commercial vehicle before I buy it?',
        a: 'Start with how much permit and fitness validity is left, then whether the odometer matches the service history, the state of the CNG kit and its certification if fitted, any pending challans or loan hypothecation on the RC, and finally the body and engine condition. Our inspection checklist guide walks through each one in order.',
      },
      {
        q: 'Can I buy a vehicle registered in another state?',
        a: 'Yes, and it is common — but an interstate purchase means an NOC from the registering RTO and re-registration in your state, which takes longer and costs more than a local transfer. Factor that into the price. Tell our team which state you are registering in and we will tell you what the specific vehicle needs.',
      },
      {
        q: 'Who handles the RC transfer after a sale?',
        a: 'The BST team supports the paperwork on both sides — we track the transfer through to completion rather than handing you a set of forms at the point of sale. Timelines depend on the RTO and on whether the vehicle carries a loan that must be closed first. Our Delhi RC transfer guide covers the process and documents in detail.',
      },
    ],
  },
  {
    id: 'selling',
    heading: 'Selling and listing your vehicle',
    faqs: [
      {
        q: 'Why is my listing not live yet?',
        a: 'Because it is still being verified. A submitted vehicle sits as pending until our team has checked it and its documents, then moves to live — it does not publish the moment you hit submit. You can follow the status of each of your vehicles from your seller dashboard, where a listing shows as pending, live, reserved, sold or rejected.',
      },
      {
        q: 'How should I price my vehicle before listing it?',
        a: 'Use our free taxi valuation calculator — it prices on commercial rules rather than private-car depreciation, which is the mistake that leaves most taxis listed too high and sitting unsold. It accounts for model, year, kilometres, permit status and condition.',
      },
      {
        q: 'Can I edit my listing after it has been published?',
        a: 'Yes — edit any of your vehicles from your seller dashboard to correct the price, swap photos or update details as the vehicle changes. Keeping the asking price current matters more than most sellers expect: a stale price is the most common reason a listing collects views but no enquiries.',
      },
      {
        q: 'Can I sell a vehicle that still has a loan on it?',
        a: 'Yes. A hypothecated vehicle can be sold, but the loan has to be closed and the hypothecation removed from the RC before the transfer completes, and the buyer will want to see that in writing. Tell us upfront that there is a loan running so we can sequence the sale properly — our guide on selling a taxi with a loan pending covers the order of events.',
      },
      {
        q: 'What happens to my listing once the vehicle is sold?',
        a: 'Tell us and we will take it down. Live listings are meant to reflect real available stock — a sold vehicle left up wastes enquiries for you and our team, and sends buyers to a car they cannot have.',
      },
    ],
  },
  {
    id: 'dealers',
    heading: 'Dealers and fleet buyers',
    faqs: [
      {
        q: 'How do I get a dealer account on Buy Sell Taxi?',
        a: 'Register as a buyer, choose the dealer account type, and submit your business details for approval. Applications are reviewed by our team — you will see your status as pending, approved or rejected on any listing where seller contact is offered.',
      },
      {
        q: 'What does an approved dealer account get me?',
        a: 'Approved dealers can reveal seller phone numbers directly from a listing and deal at their own pace instead of going through the enquiry queue. Each reveal is logged, which is the trade for direct access.',
      },
      {
        q: 'I buy several vehicles at a time — can BST source to a specification?',
        a: 'Yes. Tell our team the model, year band, fuel type, permit status and budget you are working to and we will come back with what matches, including stock not yet listed. Call +91-9560759834 or email support@buyselltaxi.com.',
      },
    ],
  },
  {
    id: 'about',
    heading: 'About Buy Sell Taxi',
    faqs: [
      {
        q: 'Is Buy Sell Taxi a dealer or a marketplace?',
        a: 'A marketplace. Vehicles are sold by their owners and by dealers on the platform; BST verifies the listings, runs the BST Yards and supports both sides through the transaction. You are buying the seller\'s vehicle, not ours.',
      },
      {
        q: 'What is a BST Yard?',
        a: 'A physical BST location where vehicles are kept and can be inspected. It gives you somewhere to see a car and its papers in person, rather than meeting a stranger at a roadside. Current yard locations are listed on the About page.',
      },
      {
        q: 'How do I avoid being scammed when buying a used taxi?',
        a: 'Never pay an advance to someone you have not met for a vehicle you have not seen — that single rule stops most of it. Verify the RC against the chassis and engine numbers on the vehicle itself, check for pending challans and hypothecation, and complete payment only alongside the transfer paperwork. Dealing through BST rather than an anonymous classified is the point: our listings are inspected and our team is accountable for the introduction.',
      },
      {
        q: 'How do I contact Buy Sell Taxi?',
        a: 'Call +91-9560759834 or email support@buyselltaxi.com. You can also send an enquiry from any listing or use the WhatsApp button on the site — whichever reaches us, the same team picks it up.',
      },
    ],
  },
];

const ALL_FAQS = FAQ_GROUPS.flatMap((g) => g.faqs);

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: ALL_FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
      { '@type': 'ListItem', position: 2, name: 'FAQ', item: PAGE_URL },
    ],
  },
];

const FAQ = () => {
  useSEO({
    title: 'Frequently Asked Questions – Buying & Selling Taxis in India',
    description:
      'Answers on buying and selling used taxis and commercial vehicles on Buy Sell Taxi — how listings are verified, contacting sellers, permits, RC transfer, dealer accounts and BST Yards.',
    keywords:
      'buy sell taxi FAQ, used taxi buying questions India, how to buy a taxi online India, taxi RC transfer questions, commercial vehicle permit questions, BST verified listings, taxi dealer account India, sell taxi questions India',
    url: PAGE_URL,
    image: `${BASE_URL}/og/landing/faq.png`,
    jsonLd,
  });

  return (
    <div className="city-landing faq-page">

      {/* Hero */}
      <section className="city-hero">
        <div className="city-hero-content">
          <nav className="faq-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link> <span aria-hidden="true">/</span> <span>FAQ</span>
          </nav>
          <h1>Frequently Asked Questions</h1>
          <p className="city-hero-sub">
            How buying and selling works on Buy Sell Taxi — verification, seller contact,
            permits, paperwork and dealer accounts.
          </p>
        </div>
      </section>

      <section className="city-intro">
        <AnswerCapsule
          question="How does Buy Sell Taxi work?"
          answer="Buy Sell Taxi is a marketplace for used taxis and commercial vehicles in India. Listings marked BST Verified are physically inspected by our team before they go live, and buyers reach sellers through an enquiry, callback or test-drive request rather than a published phone number. Our team follows up by call or WhatsApp and supports the paperwork on both sides through to RC transfer."
        />
      </section>

      {/* On-page nav — long FAQ pages are scanned, not read */}
      <section className="faq-toc-section">
        <div className="faq-toc">
          <h2 className="faq-toc-title">Jump to a section</h2>
          <ul>
            {FAQ_GROUPS.map(({ id, heading, faqs }) => (
              <li key={id}>
                <a href={`#${id}`}>{heading}</a> <span className="faq-toc-count">{faqs.length}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Question groups */}
      {FAQ_GROUPS.map(({ id, heading, faqs }) => (
        <section key={id} id={id} className="city-faq faq-group">
          <div className="city-faq-inner">
            <h2>{heading}</h2>
            <div className="city-faq-list">
              {faqs.map(({ q, a }, i) => (
                <details key={i} className="city-faq-item">
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Where the priced / model-specific answers actually live */}
      <section className="city-intro faq-more">
        <h2>Looking for prices and model detail?</h2>
        <p>
          Those questions are answered on the pages they belong to, with current
          numbers rather than a copy kept here:
        </p>
        <ul className="faq-more-links">
          <li><Link to="/vehicles">Browse vehicles</Link> — what a used taxi costs and what to check before buying</li>
          <li><Link to="/taxi-valuation">Taxi valuation calculator</Link> — what your vehicle is worth and how taxis depreciate</li>
          <li><Link to="/sell-taxi">Sell your taxi</Link> — listing, documents and getting the best price</li>
          <li><Link to="/t-permit">T-Permit guide</Link> — permit types, cost, renewal and conversion</li>
          <li><Link to="/innova-crysta-commercial-taxi-delhi">Innova Crysta</Link>, <Link to="/fortuner-commercial-taxi-delhi">Fortuner</Link>, <Link to="/dzire-commercial-taxi-delhi">Dzire</Link> and <Link to="/ertiga-commercial-taxi-delhi">Ertiga</Link> — running costs, mileage and resale by model</li>
          <li><Link to="/blog">Guides and market reports</Link> — city markets, financing, insurance and RC transfer</li>
        </ul>
      </section>

      {/* Still stuck */}
      <section className="faq-contact">
        <div className="faq-contact-inner">
          <h2>Still have a question?</h2>
          <p>Our team answers on call, email or WhatsApp — usually within the hour.</p>
          <div className="faq-contact-actions">
            <a href="tel:+919560759834" className="city-btn-primary">Call +91-9560759834</a>
            <a href="mailto:support@buyselltaxi.com" className="city-btn-secondary">support@buyselltaxi.com</a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default FAQ;
